import { useMemo, useRef, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import type {
  PortfolioDotDto,
  PortfolioDotsBoundsDto,
  PortfolioPathStepDto,
} from "../../../api/hooks/usePortfolioBuilder";
import { formatInt, niceTicks } from "../portfolio-builder";
import { useElementSize } from "../useElementSize";

const PAD = { left: 54, right: 16, top: 12, bottom: 40 };

interface PortfolioDotsProps {
  dots: PortfolioDotDto[];
  bounds: PortfolioDotsBoundsDto;
  pickRef: number;
  /** Where the match's dot is drawn this frame, as [drawdown, avg year]. */
  pickAt: number[];
  /** The path to draw, or null when it is hidden or has nowhere to go. */
  path: PortfolioPathStepDto[] | null;
  /** A portfolio other than the match that the panel is showing. */
  selected: PortfolioDotDto | null;
  onSelect: (dot: PortfolioDotDto) => void;
  onSelectMatch: () => void;
  onSelectStep: (index: number) => void;
}

/**
 * Every portfolio in the group as a dot: further right falls further, higher grows faster. The match
 * is the filled dot, and the path runs from the starting point through each answer that moved it.
 */
export function PortfolioDots({
  dots,
  bounds,
  pickRef,
  pickAt,
  path,
  selected,
  onSelect,
  onSelectMatch,
  onSelectStep,
}: PortfolioDotsProps) {
  const { t } = useTranslation("portfolioBuilder");
  const hostRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(hostRef);
  const width = Math.max(260, size.width);
  const height = Math.max(200, size.height);

  const scale = useMemo(() => {
    const { avgYearMin, avgYearMax, realDdMax } = bounds;
    const xMax = Math.max(1, realDdMax * 1.08);
    // At least 10 points tall, centred on the dots: a group of one or two portfolios would otherwise
    // span a fraction of a point, and its whole-percent labels would repeat.
    const ySpan = Math.max(10, avgYearMax - avgYearMin);
    const yMid = (avgYearMin + avgYearMax) / 2;
    const yMin = Math.max(0, yMid - ySpan * 0.56);
    const yMax = yMid + ySpan * 0.56;
    const x = (drawdown: number) =>
      PAD.left + (Math.abs(drawdown) / xMax) * (width - PAD.left - PAD.right);
    const y = (avgYear: number) =>
      height - PAD.bottom - ((avgYear - yMin) / (yMax - yMin)) * (height - PAD.top - PAD.bottom);
    return { xMax, yMin, yMax, x, y };
  }, [bounds, width, height]);

  // Drawn once per group and size, not on every frame the match's dot moves.
  const background = useMemo(
    () => (
      <>
        {niceTicks(scale.yMin, scale.yMax, height < 300 ? 4 : 6).map((tick) => (
          <g key={`y${tick}`}>
            <line
              className="portfolio-chart-grid"
              x1={PAD.left}
              x2={width - PAD.right}
              y1={scale.y(tick)}
              y2={scale.y(tick)}
            />
            <text className="portfolio-chart-tick" x={PAD.left - 7} y={scale.y(tick) + 4} textAnchor="end">
              {formatInt(tick)}%
            </text>
          </g>
        ))}
        {niceTicks(0, scale.xMax, width < 520 ? 4 : 7).map((tick) => (
          <g key={`x${tick}`}>
            <line
              className="portfolio-chart-grid"
              x1={scale.x(tick)}
              x2={scale.x(tick)}
              y1={PAD.top}
              y2={height - PAD.bottom}
            />
            <text className="portfolio-chart-tick" x={scale.x(tick)} y={height - PAD.bottom + 14} textAnchor="middle">
              {tick ? "−" : ""}
              {tick}%
            </text>
          </g>
        ))}
        <text
          className="portfolio-chart-title"
          x={(PAD.left + width - PAD.right) / 2}
          y={height - 7}
          textAnchor="middle"
        >
          {t("dots.xAxis")}
        </text>
        <text
          className="portfolio-chart-title"
          transform={`translate(13 ${(PAD.top + height - PAD.bottom) / 2}) rotate(-90)`}
          textAnchor="middle"
        >
          {t("dots.yAxis")}
        </text>
        {dots.map((dot) =>
          dot.ref === pickRef ? null : (
            <circle
              key={dot.ref}
              className="portfolio-dot"
              cx={scale.x(dot.realDdPct).toFixed(1)}
              cy={scale.y(dot.avgYearPct).toFixed(1)}
              r={3.4}
              data-dot={dot.ref}
            />
          ),
        )}
      </>
    ),
    [dots, pickRef, scale, width, height, t],
  );

  const dotsByRef = useMemo(() => new Map(dots.map((dot) => [dot.ref, dot])), [dots]);

  // One handler for the whole cloud rather than one per dot.
  const handleClick = (event: MouseEvent<SVGSVGElement>) => {
    const target = (event.target as Element).closest("[data-dot], [data-step], [data-match]");
    if (!target) return;

    if (target.hasAttribute("data-match")) {
      onSelectMatch();
      return;
    }

    const step = target.getAttribute("data-step");
    if (step !== null) {
      onSelectStep(Number(step));
      return;
    }

    const dot = dotsByRef.get(Number(target.getAttribute("data-dot")));
    if (dot) onSelect(dot);
  };

  const pickX = scale.x(pickAt[0]);
  const pickY = scale.y(pickAt[1]);

  // The last step is the match itself, so the line ends wherever its dot is this frame.
  const pathPoints =
    path?.map((step, index) =>
      index === path.length - 1 ? [pickX, pickY] : [scale.x(step.realDdPct), scale.y(step.avgYearPct)],
    ) ?? [];

  return (
    <div ref={hostRef} className="portfolio-dots">
      {size.width > 0 && (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={t("dots.label")}
          onClick={handleClick}
        >
          {background}

          {pathPoints.length > 1 && (
            <>
              <polyline
                className="portfolio-path-line"
                points={pathPoints.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ")}
              />
              {pathPoints.slice(0, -1).map(([x, y], index) => (
                <g key={index}>
                  <circle
                    className={`portfolio-path-dot${index === 0 ? " is-start" : ""}`}
                    cx={x.toFixed(1)}
                    cy={y.toFixed(1)}
                    r={5}
                  />
                  <circle className="portfolio-hit" cx={x.toFixed(1)} cy={y.toFixed(1)} r={11} data-step={index} />
                </g>
              ))}
              <text className="portfolio-path-label" x={(pathPoints[0][0] + 9).toFixed(1)} y={(pathPoints[0][1] - 8).toFixed(1)}>
                {t("dots.start")}
              </text>
            </>
          )}

          {selected && (
            <circle
              className="portfolio-selected-ring"
              cx={scale.x(selected.realDdPct)}
              cy={scale.y(selected.avgYearPct)}
              r={10}
            />
          )}

          <circle className="portfolio-pick-ring" cx={pickX} cy={pickY} r={11} />
          <circle className="portfolio-pick" cx={pickX} cy={pickY} r={7} />
          <circle className="portfolio-hit" cx={pickX} cy={pickY} r={13} data-match="" />
        </svg>
      )}
    </div>
  );
}
